import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  MenuItem,
  Select,
} from "@mui/material";
import { Formik } from "formik";

import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiInput from "components/VuiInput";
import VuiButton from "components/VuiButton";

import { AddUserSchema } from "validations";
import useCommon from "hooks/useCommon";
import { useEffect } from "react";
import { getRoles } from "store/slice/rolesSlice";
import VuiAlert from "components/VuiAlert";
import VuiAutocomplete from "components/AutoComplete";
import VuiPhoneInput from "components/VuiPhoneInput";

const AddUserDialog = ({ open, onClose, onSave }) => {
  const { DISPATCH, NAVIFATE, STORE } = useCommon();
  const { createLoading, createError, createSuccess } = STORE((state) => state.user);
  const { hotelId, hotels } = STORE((state) => state.ui);
  const { roles } = STORE((state) => state.roles);

  useEffect(() => {
    DISPATCH(getRoles({ hotelId: hotelId }));
  }, [hotelId]);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <VuiTypography variant="h6">Add New User</VuiTypography>
      </DialogTitle>

      <Formik
        initialValues={{
          name: "",
          email: "",
          contactNumber: "",
          roleId: "",
          hotelId: hotelId ? [hotelId] : [],
          type: "roleUser",
        }}
        validationSchema={AddUserSchema}
        onSubmit={(values, { resetForm }) => {
          onSave(values);
          if (createSuccess) {
            resetForm();
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleSubmit,
          setFieldTouched,
          setFieldValue,
        }) => (
          <>
            <DialogContent>
              {createError && (
                <VuiAlert color="error" dismissible size="sm">
                  {createError.message}
                </VuiAlert>
              )}
              <VuiBox mb={1}>
                <VuiTypography variant="caption" ml={1}>
                  Full Name
                </VuiTypography>
                <VuiInput
                  textOnly
                  gradient
                  fullWidth
                  name="name"
                  value={values.name}
                  onChange={handleChange}
                  error={touched.name && !!errors.name}
                  inputProps={{ maxLength: 30 }}
                />
                {touched.name && errors.name && (
                  <VuiTypography variant="caption" color="error">
                    {errors.name}
                  </VuiTypography>
                )}
              </VuiBox>

              <VuiBox mb={1}>
                <VuiTypography variant="caption" ml={1}>
                  Email
                </VuiTypography>
                <VuiInput
                  gradient
                  fullWidth
                  name="email"
                  type="email"
                  value={values.email}
                  onChange={handleChange}
                  error={touched.email && !!errors.email}
                />
                {touched.email && errors.email && (
                  <VuiTypography variant="caption" color="error">
                    {errors.email}
                  </VuiTypography>
                )}
              </VuiBox>

              <VuiBox mb={1}>
                {/* <VuiPhoneInput
                  label="Contact Number"
                  value={values.contactNumber}
                  defaultCountry="US"
                  onChange={(value) => {
                    handleChange({
                      target: {
                        name: "contactNumber",
                        value,
                      },
                    });
                  }}
                  error={touched.contactNumber && errors.contactNumber}
                /> */}
                <VuiPhoneInput
                  label="Contact Number"
                  value={values.contactNumber}
                  defaultCountry="US"
                  onChange={(value) => {
                    setFieldValue("contactNumber", value || "");
                  }}
                  onBlur={() => setFieldTouched("contactNumber", true)}
                  error={touched.contactNumber && errors.contactNumber}
                />
              </VuiBox>

              <VuiBox mb={2}>
                <VuiTypography variant="caption" ml={1}>
                  Role
                </VuiTypography>

                <VuiAutocomplete
                  options={roles}
                  value={roles.find((r) => r._id === values.roleId) || null}
                  getOptionLabel={(option) => option.title}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  placeholder="Select role"
                  gradient
                  onChange={(_, selected) => {
                    handleChange({
                      target: {
                        name: "roleId",
                        value: selected ? selected._id : "",
                      },
                    });
                  }}
                />

                {touched.roleId && errors.roleId && (
                  <VuiTypography variant="caption" color="error" ml={1}>
                    {errors.roleId}
                  </VuiTypography>
                )}
              </VuiBox>

              <VuiBox mb={2}>
                <VuiTypography variant="caption" ml={1}>
                  Assign Hotels
                </VuiTypography>

                <VuiAutocomplete
                  multiple
                  options={hotels || []}
                  value={hotels.filter((h) => values.hotelId.includes(h._id))}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(opt, val) => opt._id === val._id}
                  placeholder="Assign hotels"
                  gradient
                  onChange={(_, selected) => {
                    handleChange({
                      target: {
                        name: "hotelId",
                        value: selected.map((h) => h._id),
                      },
                    });
                  }}
                />

                {touched.hotelId && errors.hotelId && (
                  <VuiTypography variant="caption" color="error" ml={1}>
                    {errors.hotelId}
                  </VuiTypography>
                )}
              </VuiBox>
            </DialogContent>

            <DialogActions>
              <VuiButton variant="outlined" color="primary" onClick={onClose}>
                Cancel
              </VuiButton>

              <VuiButton color="primary" onClick={handleSubmit} disabled={createLoading}>
                {createLoading ? "Adding..." : "Add User"}
              </VuiButton>
            </DialogActions>
          </>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddUserDialog;
